import React from 'react';
import { formatCurrency } from '../../../../utils/format';
import { formatDate } from '../../../../utils/date';
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from '../../../../constants/financial';
import { NeoBadge, NeoFinancialTable } from '../../../../components/Common';

/**
 * CashFlowTable
 * Fluxo de caixa dia a dia usando NeoFinancialTable.
 * Cada linha = um dia; expansão mostra os registros individuais daquele dia.
 */
export const CashFlowTable = ({
    projectedDays,
    loading,
    currentTotalBalance,
    bankAccountsCount,
    hasSearched,
}) => {

    // Totais do período para o summary
    const totals = projectedDays.reduce(
        (acc, day) => ({
            income: acc.income + day.income,
            expense: acc.expense + day.expense,
        }),
        { income: 0, expense: 0 }
    );

    const lastProjectedBalance = projectedDays.length > 0
        ? projectedDays[projectedDays.length - 1].projectedBalance
        : currentTotalBalance;

    const getStatusBadge = (status) => {
        const map = {
            [FINANCIAL_STATUS.PAID]: { label: 'Pago', color: 'success' },
            [FINANCIAL_STATUS.PENDING]: { label: 'Pendente', color: 'warning' },
            [FINANCIAL_STATUS.OVERDUE]: { label: 'Atrasado', color: 'danger' },
        };
        const s = map[status] || map[FINANCIAL_STATUS.PENDING];
        return <NeoBadge color={s.color} solid={false}>{s.label}</NeoBadge>;
    };

    // ── Colunas ──
    const cashFlowColumns = [
        {
            key: 'date',
            label: 'Data',
            render: (row) => (
                <div className="d-flex align-items-center gap-2">
                    {row.isOverdue ? (
                        <NeoBadge color="danger" solid>
                            {row.displayDate}
                        </NeoBadge>
                    ) : (
                        <span className="fw-semibold font-size-13 text-dark">
                            {row.displayDate}
                        </span>
                    )}
                    {row.records.length > 0 && (
                        <small className="text-muted font-size-11">({row.records.length})</small>
                    )}
                </div>
            )
        },
        {
            key: 'income',
            label: 'Entradas',
            width: '130px',
            align: 'right',
            render: (row) => (
                <span className={`fw-semibold font-size-13 ${row.income > 0 ? 'text-success' : 'text-muted'}`}>
                    {row.income > 0 ? formatCurrency(row.income) : '—'}
                </span>
            )
        },
        {
            key: 'expense',
            label: 'Saídas',
            width: '130px',
            align: 'right',
            render: (row) => (
                <span className={`fw-semibold font-size-13 ${row.expense > 0 ? 'text-danger' : 'text-muted'}`}>
                    {row.expense > 0 ? formatCurrency(row.expense) : '—'}
                </span>
            )
        },
        {
            key: 'netDay',
            label: 'Líquido',
            width: '130px',
            align: 'right',
            render: (row) => (
                <span className={`fw-bold font-size-13 ${row.netDay >= 0 ? 'text-success' : 'text-danger'}`}>
                    {row.netDay >= 0 ? '+' : ''}{formatCurrency(row.netDay)}
                </span>
            )
        },
        {
            key: 'projectedBalance',
            label: 'Saldo Projetado',
            width: '150px',
            align: 'right',
            render: (row) => (
                <span className={`fw-bold font-size-14 ${row.projectedBalance < 0 ? 'text-danger' : 'text-dark'}`}>
                    {formatCurrency(row.projectedBalance)}
                </span>
            )
        },
    ];

    // ── Detalhes expandidos (registros do dia) ──
    const renderRowDetails = (row) => {
        if (!row.records || row.records.length === 0) {
            return (
                <small className="text-muted font-size-12">Sem movimentos neste dia.</small>
            );
        }
        return (
            <div className="d-flex flex-column gap-2">
                {row.records.map(record => (
                    <div
                        key={record.id}
                        className="d-flex justify-content-between align-items-center pb-2 border-bottom"
                    >
                        <div>
                            <span className="fw-semibold d-block font-size-12 text-dark">
                                {record.description || 'Sem descrição'}
                                <span className="ms-2">{getStatusBadge(record.status)}</span>
                            </span>
                            <span className="text-muted font-size-11">
                                {record.entityName || 'N/A'}
                                {record.categoryName && <> • {record.categoryName}</>}
                                {record.dueDate && <> • Venc: {formatDate(record.dueDate)}</>}
                            </span>
                        </div>
                        <span className={`fw-bold font-size-13 ${record.type === FINANCIAL_TYPES.ASSET ? 'text-success' : 'text-danger'}`}>
                            {record.type === FINANCIAL_TYPES.ASSET ? '+' : '-'} {formatCurrency(record.netAmount)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // ── Summary data (só exibe após busca) ──
    const cashFlowSummary = hasSearched ? [
        {
            label: 'Saldo Atual',
            value: `${formatCurrency(currentTotalBalance)}${bankAccountsCount > 0 ? ` (${bankAccountsCount})` : ''}`,
            icon: 'mdi-bank-outline',
            color: '#1565c0',
            bg: 'rgba(21, 101, 192, 0.08)'
        },
        {
            label: 'Entradas',
            value: formatCurrency(totals.income),
            icon: 'mdi-arrow-up-bold',
            statusClass: 'status-paid'
        },
        {
            label: 'Saídas',
            value: formatCurrency(totals.expense),
            icon: 'mdi-arrow-down-bold',
            statusClass: 'status-overdue'
        },
        {
            label: 'Projetado',
            value: formatCurrency(lastProjectedBalance),
            icon: 'mdi-chart-line',
            statusClass: lastProjectedBalance < 0 ? 'status-overdue' : 'status-paid'
        },
    ] : [];

    const emptyMessage = !hasSearched ? "Pronto para buscar" : "Nenhum movimento encontrado";
    const emptySubMessage = !hasSearched
        ? "Informe o período acima e clique em 'Buscar' para carregar a projeção de fluxo de caixa."
        : "Ajuste o período ou aguarde novos lançamentos.";

    return (
        <NeoFinancialTable
            columns={cashFlowColumns}
            data={projectedDays}
            keyField="date"
            loading={loading}
            summaryData={cashFlowSummary}
            renderRowDetails={renderRowDetails}
            defaultItemsPerPage={31}
            itemsPerPageOptions={[15, 31, 60, 90]}
            emptyMessage={emptyMessage}
            emptySubMessage={emptySubMessage}
        />
    );
};

export default CashFlowTable;
